import { IInjector, Token, RegistrationToken } from './IInjector';

interface IResolver {
  resolve<T>(injector: IInjector, locals?: any, path?: any[]): T;
}

class TypeResolver implements IResolver {
  type: Function;
  dependencies: string[];
  value: any;

  constructor(type: Function, dependencies?: string[]) {
    this.type = type;
    this.dependencies = dependencies || [];
  }

  resolve<T>(injector: IInjector, locals?: any, path?: any[]): T {
    if (this.value) return this.value;

    if (path && path.indexOf(this.type) >= 0)
      throw new Error(
        `Cyclic dependency detected while resolving '${this.type}'. ${path.join(' > ')}`
      );

    path = path || [];

    path.push(this.type);

    var dependencies = this.getDependencies(injector, locals, path);
    this.value = new (this.type.bind.apply(this.type, ([this.type] as any).concat(dependencies)))();

    path.pop();

    return this.value;
  }

  private getDependencies(injector: IInjector, locals?: any, path?: any[]): any[] {
    var dependencies = this.getMergedDependencies(locals);

    if (dependencies.length < this.type.length)
      throw new Error(`Cannot resolve type '${this.type}'. Are you missing the $inject metadata?`);

    var nonLocalDependencies = dependencies.filter((dep: any) => !dep.isLocal);
    nonLocalDependencies.forEach(
      (dep: any) => (dep.value = injector.resolve(dep.name, null, path))
    );
    var resolvedDependencies = dependencies.map((dep: any) => dep.value);

    return resolvedDependencies;
  }

  private getMergedDependencies(locals?: any): any {
    var obj = this.type as any;
    var dependencyNames: string[] = this.dependencies.length
      ? this.dependencies
      : obj.$inject || [];
    var dependencies = dependencyNames.map(dep => {
      return { name: dep, value: null, isLocal: false };
    });
    var localDependencies: any[] = [];
    if (locals) {
      for (var prop in locals) {
        if (locals.hasOwnProperty(prop))
          localDependencies.push({ name: prop, value: locals[prop], isLocal: true });
      }
    }

    if (!dependencies.length) return localDependencies;

    localDependencies.forEach(local => {
      var match = dependencies.filter(dep => dep.name === local.name);
      if (match.length) {
        match[0].isLocal = true;
        match[0].value = local.value;
      }
    });

    return dependencies;
  }
}

class FactoryResolver implements IResolver {
  factory: (injector: IInjector) => any;

  constructor(factory: (injector: IInjector) => any) {
    this.factory = factory;
  }

  resolve<T>(injector: IInjector, locals?: any, path?: any[]): T {
    if (locals) throw new Error('Factory resolution does not support locals yet.');

    return this.factory(injector);
  }
}

interface IRegistration {
  token: RegistrationToken;
  resolver: IResolver;
}

export class Injector implements IInjector {
  static rootInjector: IInjector = new Injector(null);
  private parentInjector: IInjector | null;
  private registrations: IRegistration[];

  constructor(parentInjector: IInjector | null) {
    this.parentInjector = parentInjector;
    this.registrations = [];
  }

  createChildInjector(): IInjector {
    return new Injector(this);
  }

  register(token: RegistrationToken, type: Function, dependencies?: string[]): void {
    var newRegistration: IRegistration = {
      token: token,
      resolver: new TypeResolver(type),
    };
    this.createOrUpdateRegistration(token, newRegistration);
  }

  registerFactory<T>(token: RegistrationToken, factory: (injector: IInjector) => T): void {
    var newRegistration: IRegistration = {
      token: token,
      resolver: new FactoryResolver(factory),
    };
    this.createOrUpdateRegistration(token, newRegistration);
  }

  private createOrUpdateRegistration(
    token: RegistrationToken,
    newRegistration: IRegistration
  ): void {
    var existingRegistration = this.getLocalRegistration(token);
    if (existingRegistration) {
      var index = this.registrations.indexOf(existingRegistration);
      this.registrations[index] = newRegistration;
    } else {
      this.registrations.push(newRegistration);
    }
  }

  resolve<T>(token: RegistrationToken | Function, locals?: any, path?: any[]): T {
    var isString = typeof token === 'string';
    var isToken = token instanceof Token;
    var isFunction = !isString && !isToken;
    var registration: IRegistration | undefined;
    if (!isFunction) registration = this.getLocalRegistration(token as RegistrationToken);
    else
      registration = {
        token: '',
        resolver: new TypeResolver(token as Function),
      };
    if (!registration) {
      if (this.parentInjector) return this.parentInjector.resolve<T>(token, locals, path);

      throw new Error(
        `Cannot resolve type '${token.toString()}'. No token by that name is registered with the Injector.`
      );
    }

    return registration.resolver.resolve<T>(this, locals, path);
  }

  private getLocalRegistration(token: RegistrationToken): IRegistration | undefined {
    for (var i = 0; i < this.registrations.length; i++) {
      var registration = this.registrations[i];
      if (registration.token === token) return registration;
    }
    return undefined;
  }

  static createChildInjector(): IInjector {
    return Injector.rootInjector.createChildInjector();
  }

  static register(token: RegistrationToken, type: Function, dependencies?: any[]): void {
    Injector.rootInjector.register(token, type, dependencies);
  }

  static registerFactory<T>(token: RegistrationToken, factory: (injector: IInjector) => T): void {
    Injector.rootInjector.registerFactory<T>(token, factory);
  }

  static resolve<T>(token: RegistrationToken | Function, locals?: any, path?: any[]): T {
    return Injector.rootInjector.resolve<T>(token, locals);
  }
}
